/**
 * A note being typed, and the rules for whether it may be signed yet.
 *
 * **In memory only.** DECISIONS.md #38: nothing about a draft is persisted, so this is a
 * plain value held in React state. It is a module of its own — rather than logic inside
 * the editor — because these rules mirror the server's and want testing without a DOM.
 *
 * **The mirror is deliberate duplication, and the server is the authority.** The same
 * relationship `NoteContent.Validate` has with migration 0007's CHECKs: the far side
 * enforces, this side turns a mistake into a disabled button and a hint instead of a
 * round trip that answers `NOTE_EMPTY` after a clinician has typed. Whenever the two
 * disagree the server wins, and the editor renders whatever code comes back — which is
 * why every refusal code still has a catalogue entry even for the ones checked here.
 */

import type { NoteSubmission } from './noteApi'

/**
 * Longest accepted content field, in characters.
 *
 * `NoteContent.MaximumFieldLength`. Duplicated, and it has to stay in step: the server
 * refuses with `NOTE_FIELD_TOO_LONG` and its `maximum` param, and the editor renders
 * *that* number rather than this one when it is refused, so a drift shows up as a
 * counter that disagrees with the error rather than as silent truncation.
 */
export const MAXIMUM_FIELD_LENGTH = 10_000

/**
 * The note types the interface offers.
 *
 * **The vocabulary is open in the database** — `note_type` has no CHECK against a list
 * (migration 0007) — so this is what the interface offers, not what the record permits.
 * `noteTypeLabel` in `patient/patientOverviewDisplay.ts` already renders these three and
 * falls back to the code for anything else, which is what lets a later type reach the
 * screen without a frontend change.
 */
export const OFFERED_NOTE_TYPES = ['daily', 'admission', 'discharge'] as const

/**
 * The language a note's text is written in — a choice beside the text (DECISIONS.md #54).
 *
 * **Never taken from the interface language.** This records what the text *is*: a clinician writing
 * Swedish under an English menu must not mark their note English. So under the Swedish interface a
 * new note starts at `sv` — `NoteContent.DefaultLanguage`, and what every note was until #54 — and
 * under any other interface it starts **unchosen**, and cannot be signed until somebody picks. That
 * is exactly when either default could be wrong: a visiting team writing Spanish, or a Swedish
 * clinician who switched the menus to read them.
 *
 * The offered languages are the interface's catalogues, labelled by their own names. A correction
 * starts from the language of the note it corrects.
 */
export const DEFAULT_NOTE_LANGUAGE = 'sv'

/** The draft's language before anybody has chosen one. Never sent. */
export const UNCHOSEN_LANGUAGE = ''

/** The language a new note starts in under this interface language. */
export function initialNoteLanguage(interfaceLanguage: string): string {
  return interfaceLanguage === DEFAULT_NOTE_LANGUAGE ? DEFAULT_NOTE_LANGUAGE : UNCHOSEN_LANGUAGE
}

/** The five content fields, in the order the form shows them. */
export const NOTE_FIELDS = [
  'subjective',
  'objective',
  'assessment',
  'plan',
  'bodyText',
] as const

export type NoteField = (typeof NOTE_FIELDS)[number]

/** A note as typed: the five fields plus the type, all as raw strings. */
export interface NoteDraft {
  readonly noteType: string

  /** Two lower-case letters, or {@link UNCHOSEN_LANGUAGE}. */
  readonly language: string
  readonly subjective: string
  readonly objective: string
  readonly assessment: string
  readonly plan: string
  readonly bodyText: string
}

/** An empty draft of the given type. */
export function emptyDraft(
  noteType: string = OFFERED_NOTE_TYPES[0],
  language: string = DEFAULT_NOTE_LANGUAGE,
): NoteDraft {
  return {
    noteType,
    language,
    subjective: '',
    objective: '',
    assessment: '',
    plan: '',
    bodyText: '',
  }
}

/**
 * A draft prefilled from a note version, for a correction.
 *
 * **Prefilled rather than blank**, because a correction usually changes one sentence: a
 * clinician who has to retype four SOAP sections to fix a dose has been given a reason
 * to leave the error in the record. Nulls become empty strings, which is the round trip
 * `trimmedOrNull` reverses.
 */
export function draftFrom(version: {
  readonly noteType: string
  readonly language: string
  readonly subjective: string | null
  readonly objective: string | null
  readonly assessment: string | null
  readonly plan: string | null
  readonly bodyText: string | null
}): NoteDraft {
  return {
    noteType: version.noteType,
    // The corrected note's own language: correcting a sentence does not change what it is written in.
    language: version.language,
    subjective: version.subjective ?? '',
    objective: version.objective ?? '',
    assessment: version.assessment ?? '',
    plan: version.plan ?? '',
    bodyText: version.bodyText ?? '',
  }
}

/** Trimmed, or null when there is nothing left. `NoteContent.Normalise`. */
export function trimmedOrNull(value: string): string | null {
  const trimmed = value.trim()

  return trimmed === '' ? null : trimmed
}

/**
 * Why a draft cannot be signed yet, or null when it can.
 *
 * The same codes the server uses, so that the catalogue has one entry per code and the
 * screen renders a locally-detected `NOTE_EMPTY` and a server-sent one identically. The
 * clinician must not be able to tell which side noticed.
 */
export type DraftProblem =
  | { readonly code: 'NOTE_TYPE_MISSING' }
  | { readonly code: 'NOTE_LANGUAGE_MISSING' }
  | { readonly code: 'NOTE_EMPTY' }
  | {
      readonly code: 'NOTE_FIELD_TOO_LONG'
      readonly field: NoteField
      readonly maximum: number
    }

/**
 * Checks a draft the way the server will.
 *
 * Order matters and matches `NoteContent.Validate`: what the note is, then whether
 * anything was written, then the shapes. One problem at a time, because the form has one
 * place to put it and the first is the one to fix.
 *
 * `NOTE_TYPE_INVALID` is not reachable from the editor — the type comes from a select
 * over {@link OFFERED_NOTE_TYPES} — so it is not checked here. It still has a catalogue
 * entry, because the server can still send it.
 */
export function draftProblem(draft: NoteDraft): DraftProblem | null {
  if (trimmedOrNull(draft.noteType) === null) {
    return { code: 'NOTE_TYPE_MISSING' }
  }

  // This side only: the server would default an absent language to Swedish, which is the mislabel
  // the choice exists to prevent, so an unchosen one is never sent.
  if (draft.language === UNCHOSEN_LANGUAGE) {
    return { code: 'NOTE_LANGUAGE_MISSING' }
  }

  const values = NOTE_FIELDS.map((field) => ({
    field,
    value: trimmedOrNull(draft[field]),
  }))

  if (values.every((entry) => entry.value === null)) {
    return { code: 'NOTE_EMPTY' }
  }

  for (const entry of values) {
    if (entry.value !== null && entry.value.length > MAXIMUM_FIELD_LENGTH) {
      return {
        code: 'NOTE_FIELD_TOO_LONG',
        field: entry.field,
        maximum: MAXIMUM_FIELD_LENGTH,
      }
    }
  }

  return null
}

/** Whether anything has been typed at all — what an "unsaved text" warning asks. */
export function isDraftDirty(draft: NoteDraft, against: NoteDraft): boolean {
  return (
    draft.noteType !== against.noteType ||
    draft.language !== against.language ||
    NOTE_FIELDS.some((field) => draft[field].trim() !== against[field].trim())
  )
}

/**
 * The draft as the API wants it.
 *
 * Trimmed, with whitespace-only fields as null: a note whose plan is three spaces is a
 * note with no plan, and sending the spaces would make the difference invisible on the
 * screen and visible in the hash. The server does this too — doing it here as well means
 * what is sent is what was meant, rather than relying on the far side to tidy.
 */
export function toSubmission(draft: NoteDraft): NoteSubmission {
  return {
    noteType: draft.noteType.trim(),
    subjective: trimmedOrNull(draft.subjective),
    objective: trimmedOrNull(draft.objective),
    assessment: trimmedOrNull(draft.assessment),
    plan: trimmedOrNull(draft.plan),
    bodyText: trimmedOrNull(draft.bodyText),
    language: draft.language,
  }
}
